import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, filter, firstValueFrom, take } from 'rxjs';
import { MachineDoc } from '@models';
import { DocsActions } from './docs.actions';
import { docsFeature } from './docs.reducer';
import { DocStatus } from './docs.models';
import { docKey } from './docs.normalize';
import { selectDoc, selectDocData, selectDocStatus } from './docs.selectors';

@Injectable({ providedIn: 'root' })
export class DocsFacade {
  private store = inject(Store);
  private actions$ = inject(Actions);

  private entities = toSignal(this.store.select(docsFeature.selectEntities), { requireSync: true });
  private keyIndex = toSignal(this.store.select(docsFeature.selectKeyIndex), { requireSync: true });

  doc$(projectId: string, key: string): Observable<MachineDoc | null> {
    return this.store.select(selectDoc(projectId, key));
  }
  data$(projectId: string, key: string): Observable<any> {
    return this.store.select(selectDocData(projectId, key));
  }
  status$(projectId: string, key: string): Observable<DocStatus> {
    return this.store.select(selectDocStatus(projectId, key));
  }
  load(projectId: string, key: string): void {
    this.store.dispatch(DocsActions.loadDoc({ projectId, key }));
  }
  save(projectId: string, key: string, data: any): void {
    this.store.dispatch(DocsActions.saveDoc({ projectId, key, data }));
  }
  applyLocalData(projectId: string, key: string, data: any): void {
    this.store.dispatch(DocsActions.applyLocalData({ projectId, key, data }));
  }
  clearProject(projectId: string): void {
    this.store.dispatch(DocsActions.clearProject({ projectId }));
  }

  // --- Imperative-compatible API (ports the legacy MachineDocsStore surface
  // onto the NgRx docs slice so callers can keep their await/read flow). ---

  /** Reactive read of a single cached doc, mirroring the old `byProjectAndKey`. */
  byProjectAndKey(projectId: string, key: string): Signal<MachineDoc | null> {
    return computed(() => {
      const e = this.entities();
      const k = this.keyIndex();
      const id = k[docKey(projectId, key)];
      return id ? (e.entities[id] ?? null) : null;
    });
  }

  /** All cached docs (defensive accessor used by Home's diagnostics). */
  all(): MachineDoc[] {
    const e = this.entities();
    return (e.ids as string[]).map((id) => e.entities[id]).filter(Boolean) as MachineDoc[];
  }

  /**
   * The new docs slice loads lazily per key, so there is no bulk warm-up; callers
   * that previously called this now resolve immediately and rely on per-key loads.
   */
  async loadProjectDocs(_projectId: string): Promise<void> {
    return;
  }

  /** Dispatch a load and resolve once the slice reports success/failure for this key. */
  async loadOne(projectId: string, key: string): Promise<MachineDoc | null> {
    if (!projectId || !key) return null;
    const settled = firstValueFrom(
      this.actions$.pipe(
        ofType(DocsActions.loadDocSucceeded, DocsActions.loadDocFailed),
        filter((a: any) => a.projectId === projectId && a.key === key),
        take(1)
      )
    );
    this.store.dispatch(DocsActions.loadDoc({ projectId, key }));
    const result: any = await settled;
    if (result?.type === DocsActions.loadDocFailed.type) {
      return this.byProjectAndKey(projectId, key)();
    }
    return (result?.doc ?? null) as MachineDoc | null;
  }

  /** Optimistic write-through save; resolves with the persisted doc. */
  async upsert(projectId: string, key: string, data: any): Promise<MachineDoc> {
    if (!projectId || !key) throw new Error('projectId and key are required');
    const settled = firstValueFrom(
      this.actions$.pipe(
        ofType(DocsActions.saveDocSucceeded, DocsActions.saveDocFailed),
        filter((a: any) => a.projectId === projectId && a.key === key),
        take(1)
      )
    );
    this.store.dispatch(DocsActions.applyLocalData({ projectId, key, data }));
    this.store.dispatch(DocsActions.saveDoc({ projectId, key, data }));
    const result: any = await settled;
    if (result?.type === DocsActions.saveDocFailed.type) {
      throw new Error(String(result?.error ?? 'Failed to save doc'));
    }
    return result.doc as MachineDoc;
  }
}
